import moment from "moment";
import React from "react";
import { formatDateString } from "../../helpers/util";

// Helper to convert moment or string to Date
function toDate(value) {
  if (!value) return null;
  if (moment.isMoment(value)) return value.toDate();
  if (typeof value === "string") {
    // First try to parse as local date (MM/DD/YYYY format)
    const localM = moment(value, "MM/DD/YYYY", true);
    if (localM.isValid()) return localM.toDate();

    // If that fails, try to parse as ISO date (from backend)
    // Use the existing formatDateString utility to handle UTC conversion
    if (value.includes("T") || value.includes("Z")) {
      const localDateString = formatDateString(value);
      const localMoment = moment(localDateString, "MM/DD/YYYY", true);
      if (localMoment.isValid()) return localMoment.toDate();
    }

    // Fallback to direct Date parsing
    const d = new Date(value);
    if (!Number.isNaN(Number(d))) return d;
    return null;
  }
  if (value instanceof Date) return value;
  return null;
}

const DatePickerHeader = ({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  minDate,
  maxDate,
}) => {
  const months = Array.from({ length: 12 }, (_, i) => new Date(0, i).toLocaleString("default", { month: "long" }));
  const currentYear = new Date().getFullYear();

  // Calculate year range based on minDate and maxDate if provided
  let startYear, endYear;
  if (minDate && moment.isMoment(minDate)) {
    startYear = minDate.year();
  } else if (minDate) {
    startYear = moment(minDate).year();
  } else {
    startYear = currentYear - 120; // Default to 120 years ago
  }

  if (maxDate && moment.isMoment(maxDate)) {
    endYear = maxDate.year();
  } else if (maxDate) {
    endYear = moment(maxDate).year();
  } else {
    endYear = currentYear + 10; // Default to 10 years in the future
  }

  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);

  const renderOption = (value, label) => (
    <option key={value} value={value}>
      {label}
    </option>
  );

  const handleTodayClick = () => {
    const today = new Date();
    // For older react-datepicker versions, try using the functions in sequence
    // with a small delay to ensure they're processed correctly
    changeYear(today.getFullYear());
    setTimeout(() => {
      changeMonth(today.getMonth());
    }, 10);
  };

  const d = toDate(date);

  return (
    <div className="datepicker-header-container">
      <div className="datepicker-header-nav">
        <button
          aria-label="Previous month"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
          type="button"
          className="datepicker-header-arrow"
        >
          <span className="fa fa-chevron-left" aria-hidden="true"></span>
        </button>
        <select
          aria-label="Month"
          value={d.getMonth()}
          onChange={(e) => changeMonth(Number(e.target.value))}
          className="datepicker-header-select"
        >
          {months.map((month, idx) => renderOption(idx, month))}
        </select>
        <select
          aria-label="Year"
          value={d.getFullYear()}
          onChange={(e) => changeYear(Number(e.target.value))}
          className="datepicker-header-select"
        >
          {years.map((year) => renderOption(year, year))}
        </select>
        <button
          aria-label="Next month"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
          type="button"
          className="datepicker-header-arrow"
        >
          <span className="fa fa-chevron-right" aria-hidden="true"></span>
        </button>
      </div>
      <div className="datepicker-header-today-row">
        <button type="button" className="app-btn app-btn--xs" onClick={handleTodayClick}>
          Today
        </button>
      </div>
    </div>
  );
};

export default DatePickerHeader;
